import NodeValueRow from './NodeValueRow.jsx'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { Handle, Position } from '@xyflow/react'
import { useState } from 'react'
import { selectedBorder } from './selectedBorder.js'

function getBoneNames() {
  const scene = window.__AVATAR_SCENE__
  if (!scene) return []
  const names = new Set()
  scene.traverse(obj => {
    if (obj.isSkinnedMesh && obj.skeleton) {
      obj.skeleton.bones.forEach(b => { if (b.name) names.add(b.name) })
    }
  })
  return [...names].sort()
}

const inputStyle = (on) => ({
  width: '100%', boxSizing: 'border-box',
  background: '#0a1200', border: `1px solid ${on ? '#4a6018' : '#2a3010'}`,
  color: on ? '#c0e060' : '#556030',
  padding: '3px 6px', borderRadius: 3,
  fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
})

const MODE_TIPS = {
  override: 'Override: below the chosen bones the layer replaces the base, by Weight.',
  additive: "Additive: below the chosen bones the layer's movement is added on top of the base, by Weight — the base keeps playing underneath.",
}
const REF_TIPS = {
  firstFrame: "The layer clip's first frame counts as zero: a gesture that starts in a neutral pose adds just its movement. (Clips already set up as additive are used as they are.)",
  rest: "The rig's rest pose counts as zero — for clips authored as a difference from the rest pose.",
}

export function LayeredBoneBlendNode({ data, selected }) {
  // Older graphs have a single `blendBone`; it becomes the first entry.
  const blendBones  = Array.isArray(data.blendBones) ? data.blendBones : [data.blendBone ?? '']
  const blendWeight = data.blendWeight ?? 1.0
  const mode        = data.blendMode === 'additive' ? 'additive' : 'override'
  const additiveRef = data.additiveRef === 'rest' ? 'rest' : 'firstFrame'
  const [boneNames, setBoneNames] = useState([])
  const [editing, setEditing] = useState(-1) // row being typed in (its datalist shows everything)

  const setBones = (next) => data.onChange?.('blendBones', next)
  const setBone = (i, v) => setBones(blendBones.map((b, j) => (j === i ? v : b)))
  const removeBone = (i) => setBones(blendBones.length > 1 ? blendBones.filter((_, j) => j !== i) : [''])
  const addBone = () => setBones([...blendBones, ''])

  const handleFocus = (i) => {
    if (!boneNames.length) setBoneNames(getBoneNames())
    setEditing(i)
  }

  const listId = `bone-list-${data.id ?? 'lbb'}`

  return (
    <div style={{
      background: selected ? '#1a1f12' : '#111608',
      border: `2px solid ${selected ? selectedBorder('#4a6018') : '#4a6018'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 185,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>🦴</span>
        <span style={{ color: '#a0d040', fontWeight: 'bold', fontSize: 10, letterSpacing: 0.5 }}>
          LAYERED BONE BLEND
        </span>
      </div>

      {/* Base input */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, position: 'relative' }}>
        <Handle
          type="target"
          position={Position.Left}
          id="base"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }}
        />
        <span style={{ marginLeft: 14, color: '#aaa', fontSize: 11 }}>Base</span>
      </div>

      {/* Layer input */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, position: 'relative' }}>
        <Handle
          type="target"
          position={Position.Left}
          id="layer"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }}
        />
        <span style={{ marginLeft: 14, color: '#aaa', fontSize: 11 }}>Layer</span>
      </div>

      {/* Mode */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#607828', fontSize: 9, marginBottom: 3 }}>Mode</div>
        <select
          value={mode}
          title={MODE_TIPS[mode]}
          data-testid="lbb-mode"
          className={nodeControlClass()}
          onChange={e => data.onChange?.('blendMode', e.target.value)}
          style={inputStyle(true)}
        >
          <option value="override" title={MODE_TIPS.override}>Override</option>
          <option value="additive" title={MODE_TIPS.additive}>Additive</option>
        </select>
      </div>

      {mode === 'additive' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ color: '#607828', fontSize: 9, marginBottom: 3 }}>Additive Reference</div>
          <select
            value={additiveRef}
            title={REF_TIPS[additiveRef]}
            data-testid="lbb-additive-ref"
            className={nodeControlClass()}
            onChange={e => data.onChange?.('additiveRef', e.target.value)}
            style={inputStyle(true)}
          >
            <option value="firstFrame" title={REF_TIPS.firstFrame}>Clip's first frame</option>
            <option value="rest" title={REF_TIPS.rest}>Rest pose</option>
          </select>
        </div>
      )}

      {/* Blend Bones — each one and everything below it */}
      <div style={{ marginBottom: 8 }}>
        <div
          style={{ color: '#607828', fontSize: 9, marginBottom: 3 }}
          title="The layer applies to each of these bones and everything below it. Everywhere else the base plays untouched."
        >Blend Bones</div>
        <datalist id={listId}>
          {boneNames.map(n => <option key={n} value={n} />)}
        </datalist>
        {blendBones.map((bone, i) => (
          <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
            <input
              className={nodeControlClass()}
              type="text"
              list={listId}
              value={bone}
              data-testid="lbb-bone"
              onChange={e => setBone(i, e.target.value)}
              onFocus={() => handleFocus(i)}
              onBlur={() => setEditing(-1)}
              placeholder={editing === i ? '' : 'e.g. clavicle_l'}
              style={inputStyle(!!bone)}
            />
            <button
              type="button"
              title="Remove this bone"
              className={nodeControlClass()}
              onClick={() => removeBone(i)}
              style={{ background: 'none', border: '1px solid #2a3010', color: '#607828', borderRadius: 3, fontSize: 10, padding: '0 5px', cursor: 'pointer' }}
            >×</button>
          </div>
        ))}
        <button
          type="button"
          data-testid="lbb-add-bone"
          title="Add another bone branch"
          className={nodeControlClass()}
          onClick={addBone}
          style={{ background: 'none', border: '1px dashed #2a3010', color: '#607828', borderRadius: 3, fontSize: 9, padding: '2px 6px', cursor: 'pointer', width: '100%' }}
        >+ Bone</button>
      </div>

      {/* Weight slider + optional wire.
          Label row is pin + name ONLY — the value lives in NodeValueRow's entry
          field, which shows it and can be typed into. See BlendNode. */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, position: 'relative' }}>
          <Handle
            type="target"
            position={Position.Left}
            id="weight-in"
            style={{ background: '#4ecf7e', width: 8, height: 8, top: '50%', transform: 'translateY(-50%)' }}
          />
          <span style={{ marginLeft: 12, color: '#4ecf7e', fontSize: 10 }}>Weight</span>
        </div>
        {/* ENG-1121 — shared slider + typed entry, drag-safe. */}
        <NodeValueRow
          label=""
          value={blendWeight}
          min={0} max={1} step={0.01}
          testId="lbb-weight"
          labelColor="#4ecf7e"
          onChange={v => data.onChange?.('blendWeight', v)}
        />
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: '#e07040', width: 10, height: 10 }}
      />
    </div>
  )
}
